import { axiosInstance } from '../../utils/axios'

interface ApiResponse<T> {
  success: boolean
  data: T
}

export interface DirectSendResult {
  recipient: string
  from: string
  sentAt: string
}

export interface PipelineInsertResult {
  activeAlertId: number
  alertHistoryId: number
  recipient: string
  estimatedDeliverySeconds: number
}

export interface AlertHistoryRow {
  id: number
  created: string
  alertType: string
  severity: string
  title: string
  message: string | null
  deviceId: number
  sigfoxId: string
  userId: number | null
  notificationsSent: number
  isRecovery: boolean
  isTest: boolean
  sourceRawMessageId: number | null
}

export interface AlertHistoryPage {
  rows: AlertHistoryRow[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
}

export interface AlertHistoryQuery {
  page?: number
  pageSize?: number
  from?: string
  to?: string
  deviceId?: number
  sigfoxId?: string
  alertType?: string
  severity?: string
  userId?: number
  includeTests?: boolean
}

export type AdminContactType = 'EMAIL' | 'SMS'

export interface AdminAlertRecipient {
  id: number
  contactType: AdminContactType
  contact: string
  label: string | null
  enabled: boolean
  created: string | null
  updated: string | null
}

export interface AdminAlertRecipientInput {
  contactType: AdminContactType
  contact: string
  label?: string | null
  enabled?: boolean
}

export interface AdminAlertConfig {
  id: number
  enabled: boolean
  lowBatteryThreshold: number
  noPowerThreshold: number
  offlineThresholdMinutes: number
  offlineModesMonitored: 'scheduled' | 'config' | 'all'
  batteryLowEnabled: boolean
  batteryNoPowerEnabled: boolean
  offlineEnabled: boolean
  recoveryEnabled: boolean
  sensorAlertsEnabled: boolean
  retryBudget: number

  /** Signal-strength thresholds. marginDb is measured above the ~-142 dBm floor. */
  signalAlertsEnabled: boolean
  weakMarginDb: number
  degradedDropDb: number
  /** Minimum RSSI samples before the median is trusted. */
  signalMinSamples: number
  signalSustainedChecks: number

  /** Missing-readings thresholds. A RATE of lost transmissions, not a gap count. */
  missingReadingsEnabled: boolean
  missingPercentThreshold: number
  missingMinTransmissions: number
  missingSustainedChecks: number
}

/** One alert phase that can be switched on or off at runtime. */
export interface AdminFeatureFlag {
  feature: string
  label: string
  description: string
  enabled: boolean
  /** False when no row exists yet; enabling creates it. */
  exists: boolean
  updated: string | null
}

export type AlertCategory = 'offline' | 'battery' | 'signal' | 'missing_readings' | 'sensor'

export interface AlertSubscriptionRecipient {
  id: number
  contact: string
  contactType: 'EMAIL' | 'SMS'
  label: string | null
  /** Recipient-level master switch; off means they receive nothing at all. */
  enabled: boolean
  /** true = subscribed. Storage is inverted (a row mutes), the API hides that. */
  subscriptions: Record<AlertCategory, boolean>
}

export interface AlertSubscriptions {
  categories: Array<{ category: AlertCategory; label: string }>
  recipients: AlertSubscriptionRecipient[]
}

export interface AdminDeviceMuteRow {
  deviceId: number
  sigfoxId: string
  serialNumber: number
  locationName: string | null
  muted: boolean
  reason: string | null
}

const BASE = '/admin/alerts'

export const alertsApi = {
  directEmail: async (params: { to: string; body: string }): Promise<DirectSendResult> => {
    const res = await axiosInstance.post<ApiResponse<DirectSendResult>>(`${BASE}/test/direct-email`, params)
    return res.data.data
  },
  directSms: async (params: { to: string; body: string }): Promise<DirectSendResult> => {
    const res = await axiosInstance.post<ApiResponse<DirectSendResult>>(`${BASE}/test/direct-sms`, params)
    return res.data.data
  },
  pipelineEmail: async (params: { to: string; body: string }): Promise<PipelineInsertResult> => {
    const res = await axiosInstance.post<ApiResponse<PipelineInsertResult>>(`${BASE}/test/pipeline-email`, params)
    return res.data.data
  },
  pipelineSms: async (params: { to: string; body: string }): Promise<PipelineInsertResult> => {
    const res = await axiosInstance.post<ApiResponse<PipelineInsertResult>>(`${BASE}/test/pipeline-sms`, params)
    return res.data.data
  },
  history: async (query: AlertHistoryQuery): Promise<AlertHistoryPage> => {
    const res = await axiosInstance.get<ApiResponse<AlertHistoryPage>>(`${BASE}/history`, { params: query })
    return res.data.data
  },

  // --- Catch-all recipients ---
  listRecipients: async (): Promise<AdminAlertRecipient[]> => {
    const res = await axiosInstance.get<ApiResponse<AdminAlertRecipient[]>>(`${BASE}/recipients`)
    return res.data.data
  },
  createRecipient: async (input: AdminAlertRecipientInput): Promise<AdminAlertRecipient> => {
    const res = await axiosInstance.post<ApiResponse<AdminAlertRecipient>>(`${BASE}/recipients`, input)
    return res.data.data
  },
  updateRecipient: async (id: number, input: Partial<AdminAlertRecipientInput>): Promise<AdminAlertRecipient> => {
    const res = await axiosInstance.patch<ApiResponse<AdminAlertRecipient>>(`${BASE}/recipients/${id}`, input)
    return res.data.data
  },
  deleteRecipient: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${BASE}/recipients/${id}`)
  },

  // --- Catch-all config (singleton) ---
  getConfig: async (): Promise<AdminAlertConfig> => {
    const res = await axiosInstance.get<ApiResponse<AdminAlertConfig>>(`${BASE}/config`)
    return res.data.data
  },
  updateConfig: async (input: Partial<AdminAlertConfig>): Promise<AdminAlertConfig> => {
    const res = await axiosInstance.patch<ApiResponse<AdminAlertConfig>>(`${BASE}/config`, input)
    return res.data.data
  },

  // --- Feature flags (which alert phases run at all) ---
  listFeatureFlags: async (): Promise<AdminFeatureFlag[]> => {
    const res = await axiosInstance.get<ApiResponse<AdminFeatureFlag[]>>('/admin/feature-flags')
    return res.data.data
  },
  setFeatureFlag: async (feature: string, enabled: boolean): Promise<void> => {
    await axiosInstance.patch(`/admin/feature-flags/${feature}`, { enabled })
  },

  // --- Per-recipient category subscriptions ---
  getSubscriptions: async (): Promise<AlertSubscriptions> => {
    const res = await axiosInstance.get<ApiResponse<AlertSubscriptions>>(`${BASE}/subscriptions`)
    return res.data.data
  },
  setSubscription: async (
    recipientId: number,
    category: AlertCategory,
    subscribed: boolean
  ): Promise<void> => {
    await axiosInstance.patch(`${BASE}/subscriptions/${recipientId}`, {
      subscriptions: [{ category, subscribed }],
    })
  },

  // --- Catch-all device mutes ---
  listDeviceMutes: async (): Promise<AdminDeviceMuteRow[]> => {
    const res = await axiosInstance.get<ApiResponse<AdminDeviceMuteRow[]>>(`${BASE}/device-mutes`)
    return res.data.data
  },
  setDeviceMute: async (deviceId: number, muted: boolean, reason?: string | null): Promise<void> => {
    await axiosInstance.patch(`${BASE}/device-mutes/${deviceId}`, { muted, reason: reason ?? null })
  },
  unmuteAll: async (): Promise<{ unmuted: number }> => {
    const res = await axiosInstance.post<ApiResponse<{ unmuted: number }>>(`${BASE}/device-mutes/unmute-all`)
    return res.data.data
  },
}
